// ─── Matchmaking beta Duel 1v1 ────────────────────────────────────────────────
// Cascade : vrai joueur → bot DB → bot local fallback
//
// Extension points :
//  - anti-repeat : exclure le dernier adversaire avec last_opponent_name
//  - daily_duel_attacks : filtrer les joueurs ayant dépassé leur quota
//  - selected_defense_formation : exposer la formation défensive du vrai joueur
//  - ELO window : resserrer/élargir les MMR_RANGES selon la saison

import { sb } from "../../../lib/supabase";
import { BOTS } from "../config/bots";
import { getEquipmentStats, simulateBotStats } from "./combat";
import type { EquipmentStats } from "./combat";
import { LAYER_IMGS } from "../../../constants/items";
import type { AvatarLayers } from "../types/leagueDuel";

export type OpponentType = "real_user" | "db_bot" | "local_fallback_bot";

export interface DuelOpponent {
  name: string;
  mmr: number;
  opponentType: OpponentType;
  avatar: string;
  /** profile_id Supabase du joueur réel (null pour les bots). */
  profile_id?: string | null;
  /** Stats d'équipement de l'adversaire — base du calcul de combat (Beta v1). */
  stats: EquipmentStats;
  /** Calques visuels de l'adversaire (uniquement pour real_user). */
  layers?: AvatarLayers;
  /** Rotation de sorts DÉFENSIVE du joueur réel (players.equipped_spells.defense).
   *  undefined → DEFAULT_DEFENSE_ROTATION côté moteur (bots inclus). */
  defenseSpells?: string[];
  /** Uniquement présent pour les bots DB. Réservé pour future personnalisation IA. */
  botStyle?: string;
}

/** Ranges MMR essayées dans l'ordre (±N). */
const MMR_RANGES = [100, 250, 500] as const;

/** Matchs max contre le même adversaire réel par jour — au-delà, il est exclu
 *  du matchmaking et la cascade bascule sur les bots (petit pool de joueurs). */
const MAX_DAILY_VS_SAME = 3;

const LAYER_SLOTS = new Set<string>(["ball", "familiar", "shoes", "top", "head"]);

/** Construit les calques visuels d'un joueur réel depuis son inventaire équipé. */
function buildLayersFromInventory(
  inventory: { iid: string; name?: string; slot?: string }[] | undefined,
  equipped: Record<string, string | null> | undefined
): AvatarLayers {
  const layers: AvatarLayers = {};
  Object.values(equipped || {}).forEach((iid) => {
    if (!iid) return;
    const item = (inventory || []).find((x) => x.iid === iid);
    if (!item?.name || !item.slot) return;
    if (!LAYER_SLOTS.has(item.slot)) return;
    const imgUrl = LAYER_IMGS[item.name];
    if (imgUrl) (layers as Record<string, string>)[item.slot] = imgUrl;
  });
  return layers;
}

function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Adversaires réels déjà affrontés MAX_DAILY_VS_SAME fois aujourd'hui,
 * comptés depuis duel_history (attaques du joueur depuis minuit local).
 * En cas d'erreur réseau, renvoie un Set vide — le matchmaking reste fonctionnel.
 */
async function getRepeatedOpponentsToday(playerName: string): Promise<Set<string>> {
  try {
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const rows = await sb.get(
      `duel_history?attacker_name=eq.${encodeURIComponent(playerName)}&played_at=gte.${dayStart.toISOString()}&select=defender_name&limit=200`
    );
    const counts = new Map<string, number>();
    rows.forEach((r: { defender_name?: string }) => {
      if (typeof r.defender_name === "string") {
        counts.set(r.defender_name, (counts.get(r.defender_name) ?? 0) + 1);
      }
    });
    const repeated = new Set<string>();
    counts.forEach((n, name) => {
      if (n >= MAX_DAILY_VS_SAME) repeated.add(name);
    });
    if (repeated.size > 0) {
      console.log(`[Matchmaking] anti-repeat — excluded today (≥${MAX_DAILY_VS_SAME} matches):`, [...repeated].join(", "));
    }
    return repeated;
  } catch (e) {
    console.warn("[Matchmaking] anti-repeat lookup failed, no exclusion applied", e);
    return new Set();
  }
}

/**
 * Cherche un vrai joueur dans la DB dans des fenêtres MMR croissantes.
 * Exclut le joueur actuel par son nom.
 */
export async function getRealPlayerOpponent(
  playerName: string,
  playerMmr: number
): Promise<DuelOpponent | null> {
  const enc = encodeURIComponent(playerName);
  const repeated = await getRepeatedOpponentsToday(playerName);

  for (const range of MMR_RANGES) {
    const low = playerMmr - range;
    const high = playerMmr + range;

    const allRows = await sb.get(
      `players?duel_mmr=gte.${low}&duel_mmr=lte.${high}&is_bot=eq.false&name=neq.${enc}&select=name,duel_mmr,duel_reputation,avatar,profile_id,inventory,equipped,equipped_spells&limit=10`
    );
    const rows = allRows.filter((r: { name?: string }) => typeof r.name === "string" && !repeated.has(r.name));
    if (allRows.length > 0 && rows.length === 0) {
      console.log(`[Matchmaking] anti-repeat — all real players in ±${range} already faced ${MAX_DAILY_VS_SAME}× today`);
    }

    if (rows.length > 0) {
      const row = randomFrom(rows);
      const opponentMmr = typeof row.duel_mmr === "number" ? row.duel_mmr : playerMmr;
      console.log(`[Matchmaking] ✅ Real player found — range ±${range}, name: ${row.name}, MMR: ${opponentMmr}`);
      console.log("[Combat] opponent type: real_user");
      console.log("[Combat] loading real opponent equipment from Supabase");
      console.log("[Combat] real opponent equipped items:", row.equipped ?? "none");
      const stats = getEquipmentStats(row.inventory, row.equipped);
      const layers = buildLayersFromInventory(row.inventory, row.equipped);
      const hasGear = stats.technique + stats.defense + stats.physique + stats.vitesse > 0;
      if (hasGear) {
        console.log("[Combat] real opponent equipment stats:", stats);
      } else {
        console.log("[Combat] real_user equipment missing, using starter stats (0)");
      }
      const defenseSpells = Array.isArray(row.equipped_spells?.defense)
        ? (row.equipped_spells.defense as string[])
        : undefined;
      return {
        name: row.name,
        mmr: opponentMmr,
        opponentType: "real_user",
        avatar: typeof row.avatar === "string" ? row.avatar : "👤",
        profile_id: typeof row.profile_id === "string" ? row.profile_id : null,
        stats,
        layers,
        defenseSpells,
      };
    }

    console.log(
      `[Matchmaking] No real player in MMR ±${range} (${low}–${high}), expanding...`
    );
  }

  return null;
}

/**
 * Cherche un bot dans la table players (is_bot = true) proche du MMR du joueur.
 * Permet d'ajouter des bots calibrés directement depuis Supabase.
 */
export async function getBotOpponent(playerMmr: number): Promise<DuelOpponent | null> {
  const low = playerMmr - 500;
  const high = playerMmr + 500;

  const rows = await sb.get(
    `players?duel_mmr=gte.${low}&duel_mmr=lte.${high}&is_bot=eq.true&select=name,duel_mmr,avatar,bot_style&limit=20`
  );

  if (rows.length > 0) {
    // Tirage parmi les 3 bots les plus proches en MMR — adversaire crédible
    // pour l'ELO, avec un peu de variété.
    const sorted = [...rows].sort(
      (a, b) => Math.abs((a.duel_mmr ?? playerMmr) - playerMmr) - Math.abs((b.duel_mmr ?? playerMmr) - playerMmr)
    );
    const row = randomFrom(sorted.slice(0, 3));
    const opponentMmr = typeof row.duel_mmr === "number" ? row.duel_mmr : playerMmr;
    const botStats = simulateBotStats(opponentMmr);
    console.log(`[Matchmaking] 🤖 DB bot found — name: ${row.name}, MMR: ${opponentMmr}, style: ${row.bot_style ?? "none"}`);
    console.log("[Combat] opponent type: db_bot");
    console.log("[Combat] bot stats simulated:", botStats);
    return {
      name: row.name,
      mmr: opponentMmr,
      opponentType: "db_bot",
      avatar: typeof row.avatar === "string" ? row.avatar : "🤖",
      botStyle: typeof row.bot_style === "string" ? row.bot_style : undefined,
      stats: botStats,
    };
  }

  return null;
}

/**
 * Fallback local — garantit qu'un adversaire est toujours disponible.
 * Choisit le bot local (config/bots.ts) le plus proche du MMR du joueur.
 */
export function getFallbackBotOpponent(playerMmr: number): DuelOpponent {
  const sorted = [...BOTS].sort(
    (a, b) => Math.abs(a.elo - playerMmr) - Math.abs(b.elo - playerMmr)
  );
  const bot = sorted[0];
  const botStats = simulateBotStats(bot.elo);
  console.log(`[Matchmaking] 🔧 Local fallback bot — name: ${bot.name}, MMR: ${bot.elo}`);
  console.log("[Combat] opponent type: local_fallback_bot");
  console.log("[Combat] bot stats simulated:", botStats);
  return {
    name: bot.name,
    mmr: bot.elo,
    opponentType: "local_fallback_bot",
    avatar: bot.avatar,
    stats: botStats,
  };
}

/**
 * Point d'entrée principal. Cascade : vrai joueur → bot DB → bot local.
 * Loggue le type d'adversaire sélectionné et la range MMR utilisée.
 */
export async function getDuelOpponent(
  playerName: string,
  playerMmr: number
): Promise<DuelOpponent> {
  console.log(
    `[Matchmaking] 🔍 Looking for opponent — player: ${playerName}, MMR: ${playerMmr}`
  );

  const realPlayer = await getRealPlayerOpponent(playerName, playerMmr);
  if (realPlayer) {
    console.log(`[Matchmaking] → opponent type: real_user`);
    return realPlayer;
  }

  const dbBot = await getBotOpponent(playerMmr);
  if (dbBot) {
    console.log(`[Matchmaking] → opponent type: db_bot`);
    return dbBot;
  }

  const fallback = getFallbackBotOpponent(playerMmr);
  console.log(`[Matchmaking] → opponent type: local_fallback_bot`);
  return fallback;
}
